import React, { FunctionComponent, createContext, useState, useCallback, useMemo, useEffect } from "react";
import { useLocation } from "react-router";
import { OpaqueInterpolation } from "react-spring";
import { useShakeAnimation } from "../../../animations/use-shake-animation";
import { LOGIN_FORM_TYPE } from "../../../models/login-form-type";
import queryString from "query-string";
import { useGraphQL } from "../../../api/context/graphql-context";
import { LOCAL_STORAGE_KEY } from "../../../models/local-storage-keys";
import { useEmail } from "../../../hooks/use-email";
import { usePassword } from "../../../hooks/use-password";
import { useLoginMutation } from "../../../api/smartmelts-api/graphql/generated";
import { SnackbarSeverity, useSnackbar } from "../../../components/snackbar/snackbar-context";
import { getGraphqlErrorLocalized } from "../../../api/errors/graphql-error-handler";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

interface ILoginContextProviderProps {}

interface ILoginContext {
  formType: LOGIN_FORM_TYPE;
  setFormType: (formType: LOGIN_FORM_TYPE) => void;
  toggleShakeAnimation: () => void;
  shakeAnimationStyle: {
    transform: OpaqueInterpolation<string>;
  };
  initialCode?: string;
  onLogin: () => void;
  loginLoading: boolean;
  password: string;
  isPasswordValid: boolean;
  onPasswordChange: (newPassword: string) => void;
  email: string;
  isEmailValid: boolean;
  onEmailChange: (newEmail: string) => void;
}

export const LoginContext = createContext<ILoginContext>({
  formType: LOGIN_FORM_TYPE.LOGIN,
  setFormType: (formType: LOGIN_FORM_TYPE) => {},
  toggleShakeAnimation: () => {},
  shakeAnimationStyle: {} as any,
  onLogin: () => {},
  loginLoading: false,
  password: "",
  isPasswordValid: false,
  onPasswordChange: (newPassword: string) => {},
  email: "",
  isEmailValid: false,
  onEmailChange: (newEmail: string) => {},
});

export const LoginContextProvider: FunctionComponent<ILoginContextProviderProps> = (props) => {
  const location = useLocation();
  const { t } = useTranslation();
  const { setAuthorizationHeader } = useGraphQL();
  const { showSnackbar } = useSnackbar();
  const { code: initialCode, formType: initialFormType } = useMemo(
    () => getInitialLoginFormParams(location, setAuthorizationHeader, true),
    [location, setAuthorizationHeader],
  );

  const [formType, setFormType] = useState<LOGIN_FORM_TYPE>(initialFormType);
  const [animateShake, setAnimateShake] = useState<boolean>(false);

  const shakeAnimationStyle = useShakeAnimation(animateShake);
  const toggleShakeAnimation = useCallback(() => setAnimateShake(!animateShake), [setAnimateShake, animateShake]);

  const { email, isEmailValid, onEmailChange } = useEmail("");
  const { password, isPasswordValid, onPasswordChange } = usePassword("");

  useEffect(() => {
    if (formType === LOGIN_FORM_TYPE.LOGIN) {
      onEmailChange("");
      onPasswordChange("");
    }
  }, [formType, onPasswordChange, onEmailChange]);

  const [loginMutation, { loading: loginLoading }] = useLoginMutation({
    onCompleted: (data) => {
      setAuthorizationHeader(data.login.token);
      setFormType(LOGIN_FORM_TYPE.CODE);
    },
    onError: (error) => {
      toggleShakeAnimation();
      showSnackbar(getGraphqlErrorLocalized(t, "login", error), SnackbarSeverity.ERROR);
    },
  });

  const onLogin = useCallback(() => {
    if (isEmailValid && isPasswordValid) {
      localStorage.setItem(LOCAL_STORAGE_KEY.LAST_ACTION, DateTime.now().toString());
      loginMutation({
        variables: {
          email,
          password,
        },
      });
    } else {
      toggleShakeAnimation();
    }
  }, [email, password, isPasswordValid, isEmailValid, loginMutation, toggleShakeAnimation]);

  return (
    <LoginContext.Provider
      value={{
        formType,
        setFormType,
        toggleShakeAnimation,
        shakeAnimationStyle,
        initialCode,
        email,
        isEmailValid,
        onEmailChange,
        password,
        isPasswordValid,
        onPasswordChange,
        onLogin,
        loginLoading,
      }}
    >
      {props.children}
    </LoginContext.Provider>
  );
};

export const getInitialLoginFormParams = (
  location: any,
  setAuthorizationHeader: (t: string | null) => void,
  checkToken?: boolean,
) => {
  const { formType, token, code: queryCode } = queryString.parse(location.search);
  const code = !Array.isArray(queryCode) && queryCode !== null ? queryCode : undefined;

  if (!formType && !token) {
    return { formType: LOGIN_FORM_TYPE.LOGIN, code };
  }

  if (checkToken && token && !Array.isArray(token) && localStorage.getItem(LOCAL_STORAGE_KEY.ACCESS_TOKEN) !== token) {
    setAuthorizationHeader(token);
  }

  switch (formType) {
    case "password-reset":
      return { formType: LOGIN_FORM_TYPE.CHANGE_PASSWORD, code };
    case "code":
      return { formType: LOGIN_FORM_TYPE.CODE, code };
    case "register":
      return { formType: LOGIN_FORM_TYPE.REGISTER, code };
    default:
      return { formType: LOGIN_FORM_TYPE.LOGIN, code };
  }
};
